import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';

import { Button, Text, Modal, Banner } from '@shopify/polaris';

import BatchGrid from '../../partials/BatchGrid';
import LoadingSpinner from '../../partials/LoadingSpinner';
import { packedShipments, unpackableShipments, resetPackStateForBatch, clearPackState, restorePackStateForBatch, packStateBatchId, packProgressForBatch } from '../../../warehouse/data/packedShipments';
import { useGetBatchQuery, useStopPackingMutation, useMarkPackedMutation, useParkPackingMutation } from '../../../warehouse/store/batches';
import { isAuthError, SESSION_EXPIRED_MESSAGE } from '../../../warehouse/utils/errors';
import { useRequirePermission } from '../../../warehouse/utils/permissions';
import { buttonProps, useDocumentTitle } from '../../../warehouse/utils/a11y';
import { UI_COLORS } from '../../../constants';

// Totes whose shipment was unpickable at pick time get the warning colour
const UNPICKABLE_TOTE_COLOR = UI_COLORS.accent;
const TOTE_COLOR = UI_COLORS.cyan;

const PackBatch = () => {
  useDocumentTitle('Packing batch');
  const { batchId } = useParams();
  const navigate = useNavigate();
  // Redirects to Profile when the user lacks the packer permission
  const { loaded: permissionsLoaded, allowed } = useRequirePermission('packer');

  // Load the claimed batch from the backend
  const { currentData: fetchedBatch, isLoading: isLoadingBatch, isFetching: isFetchingBatch, error: batchError, isError: isBatchError } = useGetBatchQuery(batchId, { skip: !batchId });
  const selectedBatch = batchId ? fetchedBatch ?? null : null;

  // Releases the batch back to 'picked' when the packer stops
  const [stopPacking, { isLoading: isStopping }] = useStopPackingMutation();
  const [stopError, setStopError] = useState('');

  // Completes the pack and stores the results server-side - the batch
  // lands on 'complete' or 'packed_with_returns' depending on whether
  // picked items are still on the cart
  const [markPacked, { isLoading: isMarkingPacked }] = useMarkPackedMutation();
  const [completeError, setCompleteError] = useState('');
  // Totes still holding picked items after a packed_with_returns
  // completion - the packer leaves them on the cart for the admins
  const [remainingReturns, setRemainingReturns] = useState([]);

  // Parks the batch mid-pack - progress is saved and the claim released
  // so any packer can resume it
  const [parkPacking, { isLoading: isParking }] = useParkPackingMutation();
  const [parkError, setParkError] = useState('');

  // Leave-confirm / complete-pack popups
  const [popupType, setPopupType] = useState(null);

  // Re-render helper - packed/unpackable shipment marks live in module-
  // level sets that don't notify React
  const [, setFocusTick] = useState(0);

  // Shipments whose totes were marked unpickable during picking can never
  // be packed - they're resolved automatically and can't be opened
  const pickUnpickableIds = new Set(
    (selectedBatch?.unpickableTotes ?? []).map(t => t.orderShipmentId)
  );

  // Group the batch's items by order - packing happens per order
  const orders = selectedBatch
    ? [...selectedBatch.items
        .reduce((map, item) => {
          const group = map.get(item.orderShipmentId) ?? { orderShipmentId: item.orderShipmentId, items: [] };
          group.items.push(item);
          map.set(item.orderShipmentId, group);
          return map;
        }, new Map())
        .values()]
    : [];

  // Shipment pending tote confirmation before its packing screen opens
  const [confirmShipment, setConfirmShipment] = useState(null);
  // Unpackable shipment pending a "remove status" confirmation
  const [unpackableConfirm, setUnpackableConfirm] = useState(null);

  const isShipmentResolved = (orderShipmentId) =>
    packedShipments.has(orderShipmentId) ||
    unpackableShipments.has(orderShipmentId) ||
    pickUnpickableIds.has(orderShipmentId);

  // Batch is done once every shipment in it is packed or unpackable
  const allResolved =
    orders.length > 0 &&
    orders.every(o => isShipmentResolved(o.orderShipmentId));

  // Blocks navigation away while a batch is loaded - except forward into
  // this batch's shipment screens, which is part of the pack flow (the
  // RN stack kept this screen mounted underneath order-packing). The ref
  // is read at navigation time so performLeave can clear it
  // synchronously before navigating itself - without it that navigate()
  // would be intercepted by this same blocker.
  const allowLeaveRef = useRef(false);
  const blocker = useBlocker(({ nextLocation }) => {
    if (!selectedBatch || allowLeaveRef.current) return false;
    return !nextLocation.pathname.startsWith(`/warehouse/app/packing/${batchId}/`);
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setStopError('');
      setPopupType('leave-confirm');
    }
  }, [blocker.state]);

  // Plain popup dismissal abandons any intercepted navigation - the user
  // chose to stay. Switching popups (e.g. leave-confirm -> park) bypasses
  // this so the blocked navigation survives.
  const dismissPopup = () => {
    setPopupType(null);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  // Fresh pack state when a different batch is loaded - packed/unpackable
  // marks only apply to the batch that created them
  useEffect(() => {
    resetPackStateForBatch(batchId);
  }, [batchId]);

  // Resuming a parked batch restores the pack state saved in
  // packing_state so the new packer continues where the last one left
  // off. Hydrate once per saved snapshot - recorded_at stamps each park,
  // so a re-park produces a new snapshot to apply. The same snapshot is
  // also re-applied if the in-memory sets were taken over by a different
  // batch in the meantime.
  const hydratedStateKey = useRef(null);
  useEffect(() => {
    const state = fetchedBatch?.packingState;
    if (!state || !fetchedBatch) {
      hydratedStateKey.current = null;
      return;
    }
    const stateKey = `${fetchedBatch.id}:${state.recorded_at ?? ''}`;
    if (hydratedStateKey.current === stateKey && packStateBatchId() === fetchedBatch.id) return;
    hydratedStateKey.current = stateKey;
    restorePackStateForBatch(
      fetchedBatch.id,
      state.packed_shipment_ids || [],
      state.packer_unpackable_shipment_ids || [],
      state.shipment_progress
    );
    // Pack progress lives in module-level sets - bump the tick to render
    setFocusTick(t => t + 1);
  }, [fetchedBatch]);

  // Clears the in-memory shipment resolution state - it only applies to
  // this batch's pack session
  const clearShipmentState = () => {
    clearPackState();
  };

  // Resumes the blocked navigation after a successful stop/park/complete
  // - or returns to the packing queue when no navigation was pending
  const performLeave = () => {
    setPopupType(null);
    clearShipmentState();
    allowLeaveRef.current = true;
    if (blocker.state === 'blocked') {
      blocker.proceed();
    } else {
      navigate('/warehouse/app/packing');
    }
  };

  // 'Stop Packing' tells the backend to return the batch to 'picked' and
  // release this packer's claim before navigating away. If the release
  // fails the popup stays open with an error so they can retry.
  const handleStopPacking = async () => {
    if (!selectedBatch || isStopping) return;

    const result = await stopPacking(selectedBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setStopError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being packed by someone else.'
            : status === 409
              ? 'This batch is no longer being packed.'
              : 'Could not release the batch. Check your connection and try again.'
      );
      return;
    }

    setStopError('');
    performLeave();
  };

  // Clicking a shipment asks the packer to confirm they have its totes;
  // unpackable shipments instead ask whether to clear that status.
  // Shipments unpickable at pick time can't be packed at all.
  const handleOrderPress = (orderShipmentId) => {
    if (pickUnpickableIds.has(orderShipmentId)) return;
    if (unpackableShipments.has(orderShipmentId)) {
      setUnpackableConfirm(orderShipmentId);
    } else {
      setConfirmShipment(orderShipmentId);
    }
  };

  const handleTotesReady = () => {
    const orderShipmentId = confirmShipment;
    setConfirmShipment(null);
    const [orderId, shipmentId] = orderShipmentId.split('#');
    navigate(`/warehouse/app/packing/${batchId}/shipment/${orderId}/${shipmentId}`);
  };

  const handleShipmentUnpackable = () => {
    if (confirmShipment) {
      unpackableShipments.add(confirmShipment);
    }
    setConfirmShipment(null);
    setFocusTick(t => t + 1);
  };

  const handleRemoveUnpackable = () => {
    if (unpackableConfirm) {
      unpackableShipments.delete(unpackableConfirm);
    }
    setUnpackableConfirm(null);
    setFocusTick(t => t + 1);
  };

  // 'Park Batch' asks for confirmation, then saves the current pack
  // progress and releases the claim so any packer can resume the batch.
  // Called from the header or the leave guard - a blocked navigation is
  // resumed after parking so it still takes the user where they headed
  const handleParkBatch = () => {
    setParkError('');
    setPopupType('park-pack');
  };

  const handleParkPackConfirmed = async () => {
    if (!selectedBatch || isParking) return;

    const batchShipmentIds = new Set(orders.map(o => o.orderShipmentId));
    // Within-shipment checklists ride along too - a resuming packer picks
    // up half-finished shipments with their items and rules still checked
    const shipmentProgress = Object.fromEntries(
      Object.entries(packProgressForBatch(batchShipmentIds)).map(([id, progress]) => [
        id,
        {
          checked_item_ids: progress.checkedItemIds,
          checked_rule_keys: progress.checkedRuleKeys,
          rules_overridden: progress.rulesOverridden,
        },
      ])
    );
    const result = await parkPacking({
      batchId: selectedBatch.id,
      packedShipmentIds: [...packedShipments].filter(id => batchShipmentIds.has(id)),
      unpackableShipmentIds: [...unpackableShipments].filter(id => batchShipmentIds.has(id)),
      shipmentProgress,
    });

    if ('error' in result) {
      const status = result.error?.status;
      setParkError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being packed by someone else.'
            : status === 409
              ? 'This batch is no longer being packed.'
              : 'Could not park the batch. Check your connection and try again.'
      );
      return;
    }

    setParkError('');
    performLeave();
  };

  // 'Complete Batch' asks for confirmation, then marks the batch packed
  // on the backend along with which shipments were packed and which the
  // packer gave up on. Pick-unpickable shipments are already known
  // server-side via unpickable_totes.
  const handleCompleteBatch = () => {
    setCompleteError('');
    setPopupType('complete-pack');
  };

  const handleCompletePackConfirmed = async () => {
    if (!selectedBatch || isMarkingPacked) return;

    const batchShipmentIds = new Set(orders.map(o => o.orderShipmentId));
    const result = await markPacked({
      batchId: selectedBatch.id,
      packedShipmentIds: [...packedShipments].filter(id => batchShipmentIds.has(id)),
      unpackableShipmentIds: [...unpackableShipments].filter(id => batchShipmentIds.has(id)),
    });

    if ('error' in result) {
      const status = result.error?.status;
      setCompleteError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being packed by someone else.'
            : status === 409
              ? 'This batch is no longer being packed.'
              : 'Could not mark the batch packed. Check your connection and try again.'
      );
      return;
    }

    setCompleteError('');

    // Items left on the cart go through the admin returns flow - tell
    // the packer which totes to leave in place before navigating away
    const completedBatch = result.data.batch;
    if (completedBatch.status === 'packed_with_returns') {
      setRemainingReturns(completedBatch.returnItems ?? []);
      setPopupType('returns-remaining');
      return;
    }

    performLeave();
  };

  // Highlight each tote holding at least one of the shipment's items;
  // totes in unpickableTotes show the warning colour instead
  const totesForShipment = (items) => {
    const unpickablePositions = new Set(
      (selectedBatch?.unpickableTotes ?? []).map(t => `${t.row}-${t.column}`)
    );
    return [
      ...new Map(
        items.map(i => [
          `${i.row}-${i.column}`,
          {
            row: i.row,
            column: i.column,
            color: unpickablePositions.has(`${i.row}-${i.column}`) ? UNPICKABLE_TOTE_COLOR : TOTE_COLOR,
          },
        ])
      ).values(),
    ];
  };

  // The shipment awaiting tote confirmation, for the popup's tote grid
  const confirmOrder = confirmShipment
    ? orders.find(o => o.orderShipmentId === confirmShipment)
    : undefined;

  const renderOrder = (order) => {
    const isPacked = packedShipments.has(order.orderShipmentId);
    const isUnpackable = unpackableShipments.has(order.orderShipmentId);
    const isPickUnpickable = pickUnpickableIds.has(order.orderShipmentId);
    const isResolved = isPacked || isUnpackable || isPickUnpickable;
    const distinctItems = order.items.length;
    const totalUnits = order.items.reduce((sum, i) => sum + i.quantity, 0);
    // Highlight each tote holding at least one of this order's items
    const totes = totesForShipment(order.items);
    const disabled = isPacked || isPickUnpickable;

    return (
      <div
        key={order.orderShipmentId}
        className={`warehouse-batch-card${isResolved ? ' warehouse-order-resolved' : ''}${disabled ? ' warehouse-batch-card-disabled' : ''}`}
        {...buttonProps(() => handleOrderPress(order.orderShipmentId), { disabled })}
      >
        <div className="warehouse-batch-header">
          <Text as="p" fontWeight="bold">{order.orderShipmentId}</Text>
          {isPacked && <span className="warehouse-badge warehouse-badge-success">✓ Packed</span>}
          {isUnpackable && <span className="warehouse-badge warehouse-badge-danger">✕ Unpackable</span>}
          {isPickUnpickable && <span className="warehouse-badge warehouse-badge-accent">⚠ Unpickable</span>}
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Items:</span>
          <span>{distinctItems} distinct</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Units:</span>
          <span>{totalUnits}</span>
        </div>

        <div className="warehouse-batch-preview">
          <span className="warehouse-info-label">Totes with this order:</span>
          <BatchGrid
            totalRows={selectedBatch?.totalRows ?? 0}
            totalColumns={selectedBatch?.totalColumns ?? 0}
            toteCount={selectedBatch?.toteCount}
            toteSize={28}
            gap={5}
            highlightedTotes={totes}
          />
        </div>
      </div>
    );
  };

  if (!permissionsLoaded || !allowed) {
    return null;
  }

  return (
    <div className="warehouse-screen">
      <div className="warehouse-header warehouse-header-packing">
        <div className="warehouse-header-row">
          <div>
            <h2>Pack Batch</h2>
            <div className="warehouse-header-subtitle">
              {selectedBatch ? `${selectedBatch.name} - Cart ${selectedBatch.cartId || 'unknown'}` : 'Active packing sessions'}
            </div>
          </div>
          {selectedBatch && (
            <Button onClick={handleParkBatch}>Park Batch</Button>
          )}
        </div>
      </div>

      {batchId && (isLoadingBatch || (isFetchingBatch && !selectedBatch)) ? (
        <div className="warehouse-loading"><LoadingSpinner statusText="Loading batch..." /></div>
      ) : batchId && (isBatchError || !selectedBatch) ? (
        <div className="warehouse-empty">
          <h2>{isAuthError(batchError) ? 'Session expired' : 'Batch Not Found'}</h2>
          <Text as="p">
            {isAuthError(batchError)
              ? SESSION_EXPIRED_MESSAGE
              : "This batch couldn't be loaded. It may have been released or packed already."}
          </Text>
        </div>
      ) : selectedBatch ? (
        <div className="warehouse-list">
          {orders.map(renderOrder)}
          {allResolved ? (
            <Button variant="primary" onClick={handleCompleteBatch}>
              Complete Batch
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="warehouse-empty">
          <h2>No Active Packs</h2>
          <Text as="p">Start packing from the Packing queue</Text>
        </div>
      )}

      <Modal
        open={confirmShipment !== null}
        onClose={() => setConfirmShipment(null)}
        title="Confirm Totes"
      >
        <Modal.Section>
        <Text as="p">
          Confirm that you have taken out all of the totes containing
          items for shipment {confirmShipment}.
        </Text>
        {confirmOrder && (
          <div className="warehouse-popup-grid">
            <BatchGrid
              totalRows={selectedBatch?.totalRows ?? 0}
              totalColumns={selectedBatch?.totalColumns ?? 0}
              toteCount={selectedBatch?.toteCount}
              toteSize={28}
              gap={5}
              highlightedTotes={totesForShipment(confirmOrder.items)}
            />
          </div>
        )}
        <div className="warehouse-popup-buttons">
          <Button onClick={() => setConfirmShipment(null)}>Cancel</Button>
          <Button variant="primary" tone="critical" onClick={handleShipmentUnpackable}>Mark Unpackable</Button>
          <Button variant="primary" onClick={handleTotesReady}>Totes Ready</Button>
        </div>
        </Modal.Section>
      </Modal>

      <Modal
        open={unpackableConfirm !== null}
        onClose={() => setUnpackableConfirm(null)}
        title="Remove Status?"
      >
        <Modal.Section>
        <Text as="p">
          Shipment {unpackableConfirm} is marked as unpackable. Do you
          want to remove that status so it can be packed?
        </Text>
        <div className="warehouse-popup-buttons">
          <Button onClick={() => setUnpackableConfirm(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleRemoveUnpackable}>Remove Status</Button>
        </div>
        </Modal.Section>
      </Modal>

      <Modal
        open={popupType !== null}
        onClose={popupType === 'returns-remaining' ? performLeave : dismissPopup}
      >
        <Modal.Section>
        {popupType === 'leave-confirm' && (
          <>
            <h2>Leave This Batch?</h2>
            <Text as="p">
              Parking saves your progress so any packer can resume this batch. Stopping releases it back to the packing queue and discards your progress.
            </Text>
            {stopError ? (
              <Banner tone="critical"><p>{stopError}</p></Banner>
            ) : null}
            {isStopping ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Releasing batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button variant="primary" onClick={dismissPopup}>Keep Packing</Button>
                <Button onClick={handleParkBatch}>Park Batch</Button>
                <Button variant="primary" tone="critical" onClick={handleStopPacking}>Stop Packing</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'park-pack' && (
          <>
            <h2>Park Batch?</h2>
            <Text as="p">
              Parking saves your progress and releases this batch so any packer can resume it later.
            </Text>
            {parkError ? (
              <Banner tone="critical"><p>{parkError}</p></Banner>
            ) : null}
            {isParking ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Parking batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={dismissPopup}>Cancel</Button>
                <Button variant="primary" onClick={handleParkPackConfirmed}>Park Batch</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'complete-pack' && (
          <>
            <h2>Complete Packing?</h2>
            <Text as="p">
              Confirm that packing is complete and every shipment in the
              batch has been packed or marked unpackable.
            </Text>
            {completeError ? (
              <Banner tone="critical"><p>{completeError}</p></Banner>
            ) : null}
            {isMarkingPacked ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Marking batch packed..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={dismissPopup}>Cancel</Button>
                <Button variant="primary" onClick={handleCompletePackConfirmed}>Complete</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'returns-remaining' && (
          <>
            <h2>Returns Remain on Cart</h2>
            <Text as="p">
              Some picked items could not be packed. Replace these
              totes in their original positions before dropping off
              the cart - the admins will handle the returns.
            </Text>
            <div className="warehouse-popup-grid">
              <BatchGrid
                totalRows={selectedBatch?.totalRows ?? 0}
                totalColumns={selectedBatch?.totalColumns ?? 0}
                toteCount={selectedBatch?.toteCount}
                toteSize={36}
                gap={6}
                highlightedTotes={[
                  ...new Map(
                    remainingReturns.map(entry => [
                      `${entry.row}-${entry.column}`,
                      { row: entry.row, column: entry.column, color: UNPICKABLE_TOTE_COLOR },
                    ])
                  ).values(),
                ]}
              />
            </div>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={performLeave}>Done</Button>
            </div>
          </>
        )}
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default PackBatch;
